import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Camera, LogOut, Save, Upload, UserCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { apiGet, apiPatch } from '../../lib/api';
import { uploadFile } from '../../lib/upload';

const inputCls = 'w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rozops-blue/20 focus:border-rozops-blue';

export default function CustomerProfile() {
  const nav = useNavigate();
  const { profile, refreshProfile, signOut } = useAuth();
  const { selectedCustomerId, selectedProvider, loadingProviders } = useOutletContext() || {};
  const [form, setForm] = useState({ full_name: '', phone: '', email: '', profile_image: '', address: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showLogout, setShowLogout] = useState(false);

  useEffect(() => {
    if (loadingProviders) return;
    const query = selectedCustomerId && selectedCustomerId !== 'all' ? `?customer_id=${selectedCustomerId}` : '';
    setLoading(true);
    apiGet(`/customer/dashboard${query}`)
      .then((res) => {
        const customer = res.data?.customer || {};
        setForm({
          full_name: profile?.full_name || customer.full_name || '',
          phone: profile?.phone || customer.phone || '',
          email: profile?.email || customer.email || '',
          profile_image: profile?.profile_image || '',
          address: customer.address || '',
        });
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [profile, selectedCustomerId, loadingProviders]);

  const save = async (e) => {
    e.preventDefault();
    if (selectedCustomerId === 'all') {
      toast.error('Select one service provider before editing address');
      return;
    }
    setSaving(true);
    try {
      const query = selectedCustomerId ? `?customer_id=${selectedCustomerId}` : '';
      await apiPatch(`/customer/profile${query}`, form);
      await refreshProfile();
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const logout = async () => {
    await signOut();
    nav('/login', { replace: true });
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadFile('/customer/profile/photo', file);
      const url = res.data?.profile_image;
      setForm({ ...form, profile_image: url || '' });
      await refreshProfile();
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  if (loading || loadingProviders) return <div className="text-rozops-text-muted">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="customer-profile">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-heading font-bold text-2xl text-rozops-navy">Profile</h1>
        <button type="button" onClick={() => setShowLogout(true)} data-testid="customer-logout" className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-rozops-text-muted hover:text-red-500">
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>
      <form onSubmit={save} className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5 space-y-4">
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-2xl bg-rozops-blue-light text-rozops-blue overflow-hidden grid place-items-center">
            {form.profile_image ? <img src={form.profile_image} alt="Customer" className="w-full h-full object-cover" /> : <UserCircle className="w-10 h-10" />}
          </div>
          <div className="flex-1">
            <label className="block text-xs uppercase tracking-wider font-bold text-rozops-text-muted mb-1.5">Customer photo</label>
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border-2 border-rozops-blue-light px-4 py-2.5 text-sm font-semibold text-rozops-blue hover:border-rozops-blue">
              {uploading ? <Camera className="w-4 h-4 animate-pulse" /> : <Upload className="w-4 h-4" />}
              {uploading ? 'Uploading...' : 'Upload image'}
              <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="profile-photo-upload" />
            </label>
            <div className="mt-1 text-xs text-rozops-text-muted">JPG, PNG, WebP or GIF. Max 3 MB.</div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-semibold text-rozops-text mb-1.5">Name</label>
            <input required className={inputCls} value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="profile-name" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-rozops-text mb-1.5">Mobile</label>
            <input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-rozops-text mb-1.5">Email address</label>
          <input type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="profile-email" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-rozops-text mb-1.5">Home address</label>
          {selectedCustomerId === 'all' && <div className="mb-2 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-700">Select one service provider above to edit the delivery address for that provider.</div>}
          {selectedProvider?.business?.business_name && <div className="mb-2 text-xs text-rozops-text-muted">Address for {selectedProvider.business.business_name}</div>}
          <textarea disabled={selectedCustomerId === 'all'} rows={3} className={inputCls} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="profile-address" />
        </div>
        <button disabled={saving} className="bg-rozops-blue text-white rounded-xl px-4 py-2.5 font-semibold inline-flex items-center gap-2 disabled:opacity-60" data-testid="profile-save">
          <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save profile'}
        </button>
      </form>
      {showLogout && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4" onClick={() => setShowLogout(false)}>
          <div className="w-full max-w-sm rounded-3xl bg-white shadow-medium p-5" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="logout-title">
            <div className="mx-auto mb-3 h-1.5 w-12 rounded-full bg-gray-200 sm:hidden" />
            <div className="w-12 h-12 rounded-2xl bg-red-50 text-red-500 grid place-items-center mb-4">
              <LogOut className="w-6 h-6" />
            </div>
            <h2 id="logout-title" className="font-heading font-bold text-xl text-rozops-navy">Log out?</h2>
            <p className="mt-1 text-sm text-rozops-text-muted">You will need to log in again to access your RozOps account.</p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <button type="button" onClick={() => setShowLogout(false)} className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-rozops-text">
                Cancel
              </button>
              <button type="button" onClick={logout} className="rounded-xl bg-red-500 px-4 py-3 text-sm font-semibold text-white" data-testid="customer-logout-confirm">
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
